import { observable, action } from 'mobx';
import { DateTime } from 'luxon';
import { RootStore } from '../rootStore';

import {
  ADMIN_ORDERS_EXCLUDE_WITHDRAWAL,
  ADMIN_ORDERS_EXCLUDE_TRANSFER,
  ADMIN_ORDERS_EXCLUDE_FEE,
  ADMIN_ORDERS_EXECUTE_WITHDRAWAL,
  ADMIN_ORDERS_EXECUTE_TRANSFER,
  ADMIN_ORDERS_EXECUTE_FEE,
  ADMIN_ORDERS_WITHDRAWAL,
  ADMIN_ORDERS_TRANSFER,
  ADMIN_ORDERS_FEE,
  ADMIN_COMMISSION,
  ADMIN_ACCOUNTS,
  ADMIN_ACCOUNTS_SIMPLE,
  ADMIN_UNPROCESSED_TRANSFERS,
  ADMIN_DOWNLOAD_PORTFOLIOS
} from 'api/endpoints';
import { UIStore } from 'stores/uiStore';
import { SingleValueResponse } from 'common/interfaces';

interface DateValue {
  value: string;
}

export enum OrderType {
  WITHDRAWAL = 'withdrawal',
  TRANSFER = 'transfer',
  FEE = 'fee',
}

export const initialValues = {
  withdrawal: '',
  transfer: '',
  fee: '',
  commissionFrom: DateTime.local().minus({ month: 1 }).toISODate(),
  commissionTo: DateTime.local().toISODate(),
};

export class AdminCsvStore {
  rootStore: RootStore;
  uiStore: UIStore;
  executeType: string;
  @observable successDialog: boolean;
  @observable confirmDialog: boolean;
  @observable generationDialog: boolean;
  @observable generationDate: string;
  @observable generationDrive?: boolean;

  constructor(rootStore: RootStore) {
    this.successDialog = false;
    this.confirmDialog = false;
    this.generationDialog = false;
    this.generationDate = '';
    this.rootStore = rootStore;
    this.uiStore = rootStore.stores.uiStore;
    this.executeType = '';
  }

  @action showSuccessDialog = () => (this.successDialog = true);
  @action hideSuccessDialog = () => (this.successDialog = false);
  @action showConfirmDialog = (executeType: string) => {
    this.confirmDialog = true;
    this.executeType = executeType;
  };
  @action hideConfirmDialog = () => (this.confirmDialog = false);
  @action hideGenerationDialog = () => (this.generationDialog = false);

  @action
  handleOrder = async (orderType: string, data: string) => {
    const { post } = this.rootStore.stores.apiStore;
    let endpoint = '';
    switch (orderType) {
      case OrderType.WITHDRAWAL:
        endpoint = ADMIN_ORDERS_EXCLUDE_WITHDRAWAL;
        break;
      case OrderType.TRANSFER:
        endpoint = ADMIN_ORDERS_EXCLUDE_TRANSFER;
        break;
      case OrderType.FEE:
        endpoint = ADMIN_ORDERS_EXCLUDE_FEE;
        break;
    }
    const orderData = data.split(',').map((id) => id.trim());

    this.uiStore.setLoading(true);
    const response = await post<SingleValueResponse<boolean>>(endpoint, orderData);
    this.uiStore.setLoading(false);
    if (response?.data?.value === true) {
      this.showSuccessDialog();
    }
  };

  downloadOrder = async (orderType: string) => {
    const { post } = this.rootStore.stores.apiStore;
    let endpoint = '';

    switch (orderType) {
      case OrderType.WITHDRAWAL:
        endpoint = ADMIN_ORDERS_WITHDRAWAL;
        break;
      case OrderType.TRANSFER:
        endpoint = ADMIN_ORDERS_TRANSFER;
        break;
      case OrderType.FEE:
        endpoint = ADMIN_ORDERS_FEE;
        break;
    }

    this.uiStore.setLoading(true);
    const response = await post<DateValue>(endpoint);

    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = false;
      this.generationDialog = true;
    }
    this.uiStore.setLoading(false);
  };

  executeOrder = async () => {
    const { post } = this.rootStore.stores.apiStore;

    let endpoint = '';
    switch (this.executeType) {
      case OrderType.WITHDRAWAL:
        endpoint = ADMIN_ORDERS_EXECUTE_WITHDRAWAL;
        break;
      case OrderType.TRANSFER:
        endpoint = ADMIN_ORDERS_EXECUTE_TRANSFER;
        break;
      case OrderType.FEE:
        endpoint = ADMIN_ORDERS_EXECUTE_FEE;
        break;
    }

    this.uiStore.setLoading(true);
    const response = await post<DateValue>(endpoint);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = false;
      this.generationDialog = true;
    }

    this.uiStore.setLoading(false);
    this.hideConfirmDialog();
  };

  downloadCommission = async (from: string, to: string) => {
    const { post } = this.rootStore.stores.apiStore;
    this.uiStore.setLoading(true);
    const response = await post<DateValue>(ADMIN_COMMISSION, {
      from,
      to,
    });
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = false;
      this.generationDialog = true;
    }
    this.uiStore.setLoading(false);
  };

  // FIXME: c&p, this.generationDrive
  downloadAccounts = async (admin: boolean) => {
    const { post } = this.rootStore.stores.apiStore;
    this.uiStore.setLoading(true);
    const response = await post<DateValue>(admin ? ADMIN_ACCOUNTS : ADMIN_ACCOUNTS_SIMPLE);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = true;
      this.generationDialog = true;
    }
    this.uiStore.setLoading(false);
  };

  downloadUnprocessedTransfers = async () => {
    const { post } = this.rootStore.stores.apiStore;
    this.uiStore.setLoading(true);
    const response = await post<DateValue>(ADMIN_UNPROCESSED_TRANSFERS);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = false;
      this.generationDialog = true;
    }
    this.uiStore.setLoading(false);
  };

  downloadPortfolios = async () => {
    const { post } = this.rootStore.stores.apiStore;
    this.uiStore.setLoading(true);
    const response = await post<DateValue>(ADMIN_DOWNLOAD_PORTFOLIOS);
    if (response?.data) {
      this.generationDate = response.data.value;
      this.generationDrive = false;
      this.generationDialog = true;
    }
    this.uiStore.setLoading(false);
  };

}
